/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  ChangeEvent,
  useRef,
  useState,
} from 'react';
import { FieldProps } from 'formik';
import classNames from 'classnames';
import { useOutsideClickEffect } from '../../../hooks/useOutsideClickEffect';
import { useAccessibilityOptionHandlers } from '../../../hooks/useAccessibility';
import CaretIcon from '../../../icons/CaretIcon';

import styles from './CustomSelectField.module.scss';

interface OptionDataType {
  label: string
  value: string
}

export type CustomSelectFieldProps<T> = FieldProps<T> & BasicFieldProps & {
  data: OptionDataType[]
  multiple?: boolean
};

function CustomSelectField<T extends string | string[]>({
  field: {
    value,
    name,
    onChange,
    onBlur,
  },
  form,
  label,
  data,
  className,
  helper,
  multiple = false,
  disabled = false,
}: CustomSelectFieldProps<T>): JSX.Element {
  // dropdown
  const [isShownDropdown, setIsShownDropdown] = useState(false);
  function toggleDropdownVisibility() {
    if (isShownDropdown && !form?.touched?.[name]) {
      form.setFieldTouched(name, true, true);
    }
    setIsShownDropdown(!isShownDropdown);
  }

  const dropdownRef = useRef<HTMLDivElement>(null);
  useOutsideClickEffect((): void => {
    if (isShownDropdown && !form?.touched?.[name]) {
      form.setFieldTouched(name, true, true);
    }
    setIsShownDropdown(false);
  }, dropdownRef);

  // field handling
  function onCheck(e: ChangeEvent<HTMLInputElement>) {
    const { target: { value: newValue, checked } } = e;
    onBlur({ target: { name } });
    if (!multiple) {
      onChange({
        target: {
          name,
          value: newValue,
        },
      });
      setIsShownDropdown(false);
    } else if (checked) {
      onChange({
        target: {
          name,
          value: [...value as string[], newValue],
        },
      });
    } else {
      const indexOfValue = value.indexOf(newValue);
      onChange({
        target: {
          name,
          value: value.slice(0, indexOfValue).concat(value.slice(indexOfValue + 1) as string),
        },
      });
    }
  }

  // accessibility handler
  const [handleKeyDown] = useAccessibilityOptionHandlers<HTMLLabelElement>({
    onEnter: (el: HTMLLabelElement) => el?.click(),
    onSpace: (el: HTMLLabelElement) => el?.click(),
    onUp: (el: HTMLLabelElement) => {
      const nextSibling = el?.nextSibling as HTMLElement;
      nextSibling?.focus();
    },
    onDown: (el: HTMLLabelElement) => {
      const prevSibling = el?.previousSibling as HTMLElement;
      prevSibling?.focus();
    },
  });

  function computeLabel(
    values: string[],
    dataOption: OptionDataType[],
  ): string {
    return values.map((key) => (
      dataOption.find(({ value: dataValue }) => (
        dataValue === key
      ))?.label
    )).join(', ');
  }

  const valid = (form?.touched[name] || false) && !(form?.errors[name] || false);
  const invalid = (form?.touched[name] || false) && (form?.errors[name] || false);

  return (
    <div
      data-testid={`custom-select-field-${name}`}
      className={classNames(styles.customSelectField, className, {
        [styles.customSelectFieldDropdownShown]: isShownDropdown,
        [styles.customSelectFieldDisabled]: disabled,
        [styles.customSelectFieldValid]: valid,
        [styles.customSelectFieldInvalid]: invalid,
      })}
      ref={dropdownRef}
    >
      <button
        type="button"
        onClick={toggleDropdownVisibility}
        className={styles.customSelectFieldToggle}
        disabled={disabled}
      >
        {(!value && !label) && (
          <span className={styles.customSelectFieldToggleValues} />
        )}

        {(value && (!multiple || value.length)) > 0 && (
          <span className={styles.customSelectFieldToggleValues}>
            { multiple
              ? (
                value && Array.isArray(value) && (
                  computeLabel(value as string[], data)
                )
              )
              : (
                value && data.find(({ value: dataValue }) => (
                  dataValue.toString() === value.toString()
                ))?.label
              )}
          </span>
        )}

        <span
          className={classNames([styles.customSelectFieldToggleLabel], {
            [styles.customSelectFieldToggleLabelActive]: (
              value && (!multiple || value.length)
            ) > 0,
          })}
        >
          {label}
        </span>
        <CaretIcon
          className={classNames([styles.caretIcon], {
            [styles.invertedArrow]: isShownDropdown,
          })}
        />
      </button>

      { isShownDropdown && (
        <div
          className={styles.customSelectFieldDropdown}
          role="list"
        >
          {data.map((option, index) => (
            <label
              key={option.value}
              tabIndex={index}
              className={styles.customSelectFieldOption}
              onKeyDown={handleKeyDown}
            >
              <input
                id={name}
                type={multiple ? 'checkbox' : 'radio'}
                name={name}
                value={option.value}
                onChange={onCheck}
                checked={Array.isArray(value) && value.indexOf(option.value) > -1}
              />
              {multiple && (<span className="fake-checkbox" />)}
              <span className={styles.customSelectFieldOptionLabel}>
                {option.label}
              </span>
            </label>
          ))}
        </div>
      )}

      {helper && !invalid && (
        <span
          data-testid={`helper-custom-select-field-${name}`}
          className={styles.helper}
        >
          {helper}
        </span>
      )}
    </div>
  );
}

export default CustomSelectField;
